import React, { useContext, useRef, useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styles from '../Sandbox/Sandbox.module.css';
import globalStyles from '../../App.module.css';
import Board from '../Board';
import { GameContext } from '../Providers/GameContext';
import { AuthContext } from '../Providers/AuthContext';
import { useTheme } from '../Providers/ThemeContext';
import GlobalHeader from '../GlobalHeader';
import AdSenseLayout from '../AdSense/AdSenseLayout';
import WispyStars from '../Tutorials/WispyStars';
import FENHistory from '../../services/zodiacEngine/Fen/FENHistory';
import GameInfo from '../GameInfo/GameInfo';
import TurnPopover from '../TurnPopover/TurnPopover';
import { PieceType, Mode, IPiece, Position } from '../../services/zodiacEngine/types';
import PiecePalette, { PiecePaletteRef } from '../Sandbox/PiecePalette';
import ZodiacEngineService from '../ZodiacEngineService';
import { Piece } from '../../services/zodiacEngine/Piece';
import { SandboxSessionService, SandboxSession } from '../../services/firebase/sandboxSessionService';
import { playUISound } from '../../utils/UISoundEffects';

function SharedSandbox() {
  const navigate = useNavigate();
  const { sessionId } = useParams<{ sessionId: string }>();
  const { theme } = useTheme();
  const { user } = useContext(AuthContext);
  const {
    gameState,
    potentialMoves,
    potentialSideEffects,
    updateGameState
  } = useContext(GameContext);
  const fenHistoryRef = useRef<any>(null);
  const piecePaletteRef = useRef<PiecePaletteRef>(null);

  const [session, setSession] = useState<SandboxSession | null>(null);
  const [partnerName, setPartnerName] = useState('Friend');
  // Track whether WE just pushed a FEN update (so we don't reload our own change)
  const lastPushedFenRef = useRef<string | null>(null);
  const isInitializedRef = useRef(false);

  // Initialize sandbox mode
  useEffect(() => {
    const engine = ZodiacEngineService.zodiacEngine;
    engine.loadFromFEN('8/8/8/8/8');
    engine.setGameMode(Mode.Sandbox);
    isInitializedRef.current = true;
  }, []);

  // Subscribe to session and sync board state
  useEffect(() => {
    if (!sessionId || !user) return;

    const unsub = SandboxSessionService.subscribeSession(sessionId, (sess) => {
      if (!sess) {
        // Session was deleted or doesn't exist
        alert('Sandbox session not found or was closed.');
        navigate('/');
        return;
      }

      setSession(sess);

      if (sess.status === 'closed') {
        alert('Your friend closed the sandbox session.');
        navigate('/');
        return;
      }

      // Mark session as active if we're the guest joining
      if (sess.status === 'waiting' && sess.guestUid === user.uid) {
        SandboxSessionService.joinSession(sessionId);
      }

      // Sync board: only load remote FEN if it was changed by the OTHER player
      if (sess.lastUpdatedBy !== user.uid && sess.fen !== lastPushedFenRef.current && isInitializedRef.current) {
        lastPushedFenRef.current = sess.fen;
        const engine = ZodiacEngineService.zodiacEngine;
        engine.loadFromFEN(sess.fen);
        engine.setGameMode(Mode.Sandbox);

        // Sync palette with board state
        if (piecePaletteRef.current) {
          const boardPieces: Array<{type: PieceType, isLight: boolean}> = [];
          const gs = engine.getGameState();
          for (let row = 0; row < gs.board.length; row++) {
            for (let col = 0; col < gs.board[row].length; col++) {
              gs.board[row][col].Pieces.forEach((piece: any) => {
                boardPieces.push({ type: piece.type, isLight: piece.isLight });
              });
            }
          }
          piecePaletteRef.current.markBoardPiecesAsPlaced(boardPieces);
        }
      }
    });

    return () => unsub();
  }, [sessionId, user, navigate]);

  // Push local board changes to Firebase
  const pushBoardToFirebase = useCallback(() => {
    if (!sessionId || !user) return;
    const gs = ZodiacEngineService.zodiacEngine.getGameState();
    const currentFen = gs.fenHistory.length > 0
      ? gs.fenHistory[gs.fenHistory.length - 1]
      : '8/8/8/8/8';
    // Strip metadata annotations from FEN before pushing
    const cleanFen = currentFen.replace(/~\{[^}]+\}/g, '');
    lastPushedFenRef.current = cleanFen;
    SandboxSessionService.updateBoard(sessionId, cleanFen, user.uid).catch(() => {});
  }, [sessionId, user]);

  // Track previous board pieces to detect removals
  const [previousPieces, setPreviousPieces] = useState<Map<string, { type: PieceType; isLight: boolean }>>(new Map());

  useEffect(() => {
    if (gameState.mode !== Mode.Sandbox) return;

    const currentPieces = new Map<string, { type: PieceType; isLight: boolean }>();
    for (let row = 0; row < gameState.board.length; row++) {
      for (let col = 0; col < gameState.board[row].length; col++) {
        gameState.board[row][col].Pieces.forEach((piece: any) => {
          currentPieces.set(piece.id, { type: piece.type, isLight: piece.isLight });
        });
      }
    }

    if (previousPieces.size > 0) {
      previousPieces.forEach((pieceInfo, prevId) => {
        if (!currentPieces.has(prevId) && piecePaletteRef.current) {
          if (pieceInfo.type !== PieceType.Virus) {
            piecePaletteRef.current.returnCapturedPiece(pieceInfo.type, pieceInfo.isLight);
          }
        }
      });
    }

    setPreviousPieces(currentPieces);
  }, [gameState]);

  const isValidSandboxPlacement = (piece: any, targetSquare: any, _position: any): boolean => {
    const existingPieces = targetSquare.Pieces || [];
    const hasEnemyPiece = existingPieces.some((p: any) => p.isLight !== piece.isLight);
    const hasFriendlyPiece = existingPieces.some((p: any) => p.isLight === piece.isLight);

    if (piece.type === 'Virus') {
      const animalTypes = ['Bear', 'Crane', 'Frog', 'Goat', 'Monkey', 'Octopus', 'Penguin', 'Rhino', 'Snake', 'Wolf'];
      const hasVirus = existingPieces.some((p: any) => p.type === 'Virus');
      if (hasVirus) return false;
      if (existingPieces.length === 0) return true;
      return existingPieces.some((p: any) => animalTypes.includes(p.type));
    }

    if (hasFriendlyPiece) {
      const friendlyTree = existingPieces.find((p: any) => p.type === 'Tree' && p.isLight === piece.isLight);
      if (!friendlyTree) return false;
      const canClimb = ['Bear', 'Crane', 'Frog', 'Fungi', 'Goat', 'Monkey', 'Octopus', 'Snake'].includes(piece.type);
      if (!canClimb) return false;
    }

    if (hasEnemyPiece) {
      const enemyTree = existingPieces.find((p: any) => p.type === 'Tree' && p.isLight !== piece.isLight);
      if (enemyTree) {
        const canClimb = ['Bear', 'Crane', 'Frog', 'Fungi', 'Goat', 'Monkey', 'Octopus', 'Snake'].includes(piece.type);
        if (!canClimb) return false;
        if (existingPieces.length > 1) return false;
      } else {
        return false;
      }
    }

    return true;
  };

  const handlePieceDragStart = (event: React.DragEvent<HTMLImageElement>, piece: IPiece, position: Position) => {
    event.dataTransfer.setData("piece", JSON.stringify({
      type: piece.type,
      isLight: piece.isLight,
      isFromBoard: true,
      fromRow: position.row,
      fromCol: position.col,
      pieceId: piece.id,
    }));
    event.dataTransfer.effectAllowed = "move";

    const element = event.currentTarget;
    const size = Math.round(Math.max(element.offsetWidth, element.offsetHeight) * 0.85);
    const dragEl = document.createElement('div');
    dragEl.style.position = 'fixed';
    dragEl.style.top = '-1000px';
    dragEl.style.left = '-1000px';
    dragEl.style.width = `${size}px`;
    dragEl.style.height = `${size}px`;
    dragEl.style.pointerEvents = 'none';
    dragEl.style.zIndex = '-1';

    const img = document.createElement('img');
    img.src = element.src;
    img.style.width = `${size}px`;
    img.style.height = `${size}px`;
    dragEl.appendChild(img);
    document.body.appendChild(dragEl);

    event.dataTransfer.setDragImage(dragEl, size / 2, size / 2);

    element.style.opacity = '0';
    element.addEventListener('dragend', () => {
      element.style.opacity = '';
    }, { once: true });

    requestAnimationFrame(() => {
      document.body.removeChild(dragEl);
    });
  };

  const handleBoardPieceDroppedToSandbox = (_pieceType: PieceType, _isLight: boolean, fromRow: number, fromCol: number, pieceId: string) => {
    const sourceSquare = gameState.board[fromRow][fromCol];
    const pieceIndex = sourceSquare.Pieces.findIndex((p: any) => p.id === pieceId);
    if (pieceIndex === -1) return;
    sourceSquare.Pieces.splice(pieceIndex, 1);
    updateGameState({ ...gameState });
    pushBoardToFirebase();

    const audio = new Audio('/sand1.mp3');
    audio.volume = 0.3;
    audio.play().catch(() => {});
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>, position: any) => {
    event.preventDefault();
    const pieceData = event.dataTransfer.getData("piece");
    if (!pieceData) return;

    const parsedData = JSON.parse(pieceData);
    const { type, isLight, sandboxKey, isRepositioning, isFromBoard, fromRow, fromCol, pieceId } = parsedData;

    // Handle board-to-board piece repositioning
    if (isFromBoard) {
      if (fromRow === position.row && fromCol === position.col) return;

      const sourceSquare = gameState.board[fromRow][fromCol];
      const pieceIndex = sourceSquare.Pieces.findIndex((p: any) => p.id === pieceId);
      if (pieceIndex === -1) return;

      const piece = sourceSquare.Pieces[pieceIndex];
      const targetSquare = gameState.board[position.row][position.col];

      if (!isValidSandboxPlacement(piece, targetSquare, position)) return;

      sourceSquare.Pieces.splice(pieceIndex, 1);
      ZodiacEngineService.zodiacEngine.placePiece(piece, position);
      pushBoardToFirebase();

      const audio = new Audio('/turn change.mp3');
      audio.volume = 0.3;
      audio.play().catch(() => {});
      return;
    }

    if (isRepositioning) {
      const sandboxContainer = document.querySelector('[class*="paletteForeground"]');
      if (sandboxContainer) {
        const rect = sandboxContainer.getBoundingClientRect();
        const isOverSandbox = event.clientX >= rect.left &&
                             event.clientX <= rect.right &&
                             event.clientY >= rect.top &&
                             event.clientY <= rect.bottom;
        if (isOverSandbox) return;
      }
    }

    if (gameState.mode === Mode.Sandbox && piecePaletteRef.current) {
      if (piecePaletteRef.current.isPieceOnBoard(type as PieceType, isLight)) {
        if (sandboxKey && piecePaletteRef.current) {
          piecePaletteRef.current.handlePieceDropFailed(sandboxKey);
        }
        return;
      }
    }

    let placementSuccessful = false;

    try {
      if (gameState.mode !== Mode.Sandbox) {
        ZodiacEngineService.zodiacEngine.setGameMode(Mode.Sandbox);
      }

      const piece = new Piece(type as PieceType, isLight);
      if (type === 'Penguin') {
        piece.direction = isLight ? 'down' : 'up';
      }

      const targetSquare = gameState.board[position.row][position.col];
      if (!isValidSandboxPlacement(piece, targetSquare, position)) {
        throw new Error('Invalid placement');
      }

      ZodiacEngineService.zodiacEngine.placePiece(piece, position);

      if (type === 'Virus') {
        const square = gameState.board[position.row][position.col];
        const animalPiece = square.Pieces.find((p: any) => p.type !== 'Virus');
        if (animalPiece) {
          animalPiece.isInfected = true;
        }
      }

      placementSuccessful = true;
      pushBoardToFirebase();

      const audio = new Audio('/turn change.mp3');
      audio.volume = 0.3;
      audio.play().catch(() => {});
    } catch {
      placementSuccessful = false;
    }

    if (sandboxKey && piecePaletteRef.current) {
      if (placementSuccessful) {
        piecePaletteRef.current.handlePiecePlaced(sandboxKey);
      } else {
        piecePaletteRef.current.handlePieceDropFailed(sandboxKey);
      }
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  };

  const handleSquareClick = (position: any) => {
    if (!piecePaletteRef.current) return;
    const selectedPiece = piecePaletteRef.current.getSelectedPiece();
    if (!selectedPiece) return;

    if (piecePaletteRef.current.isPieceOnBoard(selectedPiece.type, selectedPiece.isLight)) return;

    try {
      if (gameState.mode !== Mode.Sandbox) {
        ZodiacEngineService.zodiacEngine.setGameMode(Mode.Sandbox);
      }

      const piece = new Piece(selectedPiece.type, selectedPiece.isLight);
      if (selectedPiece.type === 'Penguin') {
        piece.direction = selectedPiece.isLight ? 'down' : 'up';
      }

      const targetSquare = gameState.board[position.row][position.col];
      if (!isValidSandboxPlacement(piece, targetSquare, position)) return;

      ZodiacEngineService.zodiacEngine.placePiece(piece, position);

      if (selectedPiece.type === 'Virus') {
        const square = gameState.board[position.row][position.col];
        const animalPiece = square.Pieces.find((p: any) => p.type !== 'Virus');
        if (animalPiece) animalPiece.isInfected = true;
      }

      const audio = new Audio('/turn change.mp3');
      audio.volume = 0.3;
      audio.play().catch(() => {});

      const suffix = selectedPiece.isLight ? 'light' : 'dark';
      const pieceKey = `${selectedPiece.type}-${suffix}-0`;
      piecePaletteRef.current.handlePiecePlaced(pieceKey);
      piecePaletteRef.current.clearSelectedPiece();

      pushBoardToFirebase();
    } catch {
      // Placement failed silently
    }
  };

  const handleClearBoard = () => {
    const board = gameState.board;
    const piecesToReturn: Array<{type: PieceType, isLight: boolean}> = [];

    for (let row = 0; row < board.length; row++) {
      for (let col = 0; col < board[row].length; col++) {
        board[row][col].Pieces.forEach((piece: any) => {
          piecesToReturn.push({ type: piece.type, isLight: piece.isLight });
        });
        board[row][col].Pieces = [];
      }
    }

    ZodiacEngineService.zodiacEngine.loadFromFEN('8/8/8/8/8');

    if (piecePaletteRef.current) {
      piecesToReturn.forEach((pieceInfo) => {
        piecePaletteRef.current?.returnCapturedPiece(pieceInfo.type, pieceInfo.isLight);
      });
    }

    pushBoardToFirebase();

    const audio = new Audio('/sand1.mp3');
    audio.volume = 0.4;
    audio.play().catch(() => {});
  };

  const handleLeave = () => {
    if (sessionId) {
      SandboxSessionService.closeSession(sessionId).catch(() => {});
    }
    navigate('/');
  };

  const isWaiting = session?.status === 'waiting';
  const statusText = session?.status === 'active'
    ? 'Shared Sandbox'
    : 'Sandbox';

  return (
    <AdSenseLayout>
      <div className={`${styles.container} ${theme === 'dark' ? styles.dark : ''} ${globalStyles.app}`}>
        <WispyStars />
        <GlobalHeader />

        {/* Back button */}
        <button
          className={styles.homeBackButton}
          onClick={() => { playUISound(); handleLeave(); }}
          aria-label="Leave sandbox"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
        </button>

        <button
          className={styles.mobileBackButton}
          onClick={handleLeave}
          aria-label="Leave sandbox"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
        </button>

        <TurnPopover
          isThisPlayersTurn={gameState.isLightTurn === true}
          state={gameState.state}
          mode={gameState.mode}
        />

        {/* Left Panel */}
        <div className={styles.leftPanel} style={{ pointerEvents: 'auto', zIndex: 100 }}>
          <div className={styles.leftPanelContent}>
            {isWaiting ? (
              <div className={styles.waitingStatus}>
                Waiting for friend to join...
              </div>
            ) : (
              <h1 className={styles.title}>{statusText}</h1>
            )}

            <div className={styles.description}>
              <p>Both players can drag and drop pieces on the shared board.</p>
            </div>

            <button
              className={styles.clearBoardButton}
              onClick={handleClearBoard}
              title="Clear Board"
            >
              Clear Board
            </button>

            <button
              className={styles.clearBoardButton}
              onClick={() => piecePaletteRef.current?.tidyPieces()}
              title="Tidy Sandbox"
            >
              Tidy Sandbox
            </button>

            <button
              className={styles.clearBoardButton}
              onClick={handleLeave}
              title="Leave Sandbox"
              style={{ marginTop: 16, background: '#3a2020', color: '#e57373' }}
            >
              Leave Session
            </button>
          </div>
        </div>

        {/* Game Area */}
        <div className={styles.gameArea}>
          <div className={styles.gameAreaContent}>
            <div className={styles.sandImageContainer}>
              <img
                src="/images/sand_no_border.png"
                alt="Sand texture"
                className={styles.sandImage}
              />
              <PiecePalette ref={piecePaletteRef} onBoardPieceDroppedToSandbox={handleBoardPieceDroppedToSandbox} />
            </div>

            <div className={styles.boardContainer}>
              <Board
                gameState={gameState}
                uiState={{
                  potentialMoves: potentialMoves,
                  potentialSideEffects: potentialSideEffects,
                }}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onSquareClick={handleSquareClick}
                onPieceDragStart={handlePieceDragStart}
              />
            </div>

            <div className={styles.rightPanel}>
              <FENHistory ref={fenHistoryRef} />
              <GameInfo />
            </div>
          </div>
        </div>
      </div>
    </AdSenseLayout>
  );
}

export default SharedSandbox;
